import { useMemo, useState } from 'react';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UpcomingRoundedIcon from '@mui/icons-material/UpcomingRounded';
import {
  Box,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { useMerchantPortalSettingsSectionVisibility } from '@/shared/navigation/merchantPortalNavVisibility';

import { SETTINGS_GROUPS, settingsNavForGroup } from './settingsNav';

const RAIL_WIDTH = 268;
const APP_BAR_HEIGHT = 64;

/**
 * Two-pane settings shell.
 *
 * A persistent, searchable, permission-aware **category rail** (grouped) on the
 * left and the selected settings screen on the right — so an operator moves
 * between settings without bouncing back to a hub. The rail is desktop-only; on
 * smaller screens the grouped overview (`SettingsIndexPage`) and each screen's
 * back link carry navigation.
 */
export function SettingsLayout() {
  const { t } = useTranslation('settings');
  const { hasPermission } = usePermissions();
  const sectionVisibility = useMerchantPortalSettingsSectionVisibility();
  const location = useLocation();
  const [filter, setFilter] = useState('');
  const query = filter.trim().toLowerCase();

  const groups = useMemo(
    () =>
      SETTINGS_GROUPS.map((group) => ({
        group,
        items: settingsNavForGroup(group).filter(
          (nav) =>
            !nav.hiddenFromUi &&
            // Domains the employee cannot view are omitted, not dimmed. A locked row advertises a
            // capability and then refuses it — for someone whose whole job is the warehouse, that
            // turned Settings into thirty greyed-out rows with one live entry buried among them.
            // Previews stay: they have no backend at all, so nobody holds their code, and hiding
            // "not viewable" would delete the coming-soon teasers from everyone.
            (hasPermission(nav.viewPermission) || nav.preview) &&
            (!query ||
              t(nav.label).toLowerCase().includes(query) ||
              t(nav.description).toLowerCase().includes(query)),
        ),
      }))
        .filter((entry) => (entry.group.featureKey ? sectionVisibility[entry.group.featureKey] : true))
        .filter((entry) => entry.items.length > 0),
    [hasPermission, query, sectionVisibility, t],
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', minHeight: '100%' }}>
      <Box
        component="nav"
        aria-label={t('settingsLayout.navAriaLabel')}
        sx={{
          display: { xs: 'none', md: 'block' },
          flexShrink: 0,
          width: RAIL_WIDTH,
          position: 'sticky',
          top: APP_BAR_HEIGHT,
          alignSelf: 'flex-start',
          maxHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
          overflowY: 'auto',
          borderRight: (t) => `1px solid ${t.palette.divider}`,
          py: 2,
        }}
      >
        <Box sx={{ px: 2.5, pb: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {t('settingsLayout.heading')}
          </Typography>
          <TextField
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder={t('settingsLayout.searchPlaceholder')}
            size="small"
            fullWidth
            aria-label={t('settingsLayout.searchAriaLabel')}
            sx={{ mt: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        {groups.length === 0 && query ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
            {t('settingsLayout.noMatches', { filter })}
          </Typography>
        ) : groups.length === 0 ? null : (
          groups.map(({ group, items }) => (
            <List
              key={group.key}
              dense
              subheader={
                <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
                  {t(group.label)}
                </ListSubheader>
              }
              sx={{ px: 1 }}
            >
              {items.map((nav) => {
                const canView = hasPermission(nav.viewPermission);
                const selected = location.pathname === nav.path;
                const comingSoon = !canView && Boolean(nav.preview);
                const tooltipTitle = comingSoon
                  ? t('settingsLayout.comingSoon')
                  : nav.preview
                    ? t('settingsLayout.preview')
                    : undefined;

                if (!canView && !comingSoon) {
                  return (
                    <Tooltip key={nav.key} title={t('settingsLayout.noAccess')} placement="right">
                      <Box component="span" sx={{ display: 'block' }}>
                        <ListItemButton disabled sx={{ borderRadius: 2 }}>
                          <ListItemIcon sx={{ minWidth: 34 }}>{nav.icon}</ListItemIcon>
                          <ListItemText primary={t(nav.label)} />
                          <LockOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                        </ListItemButton>
                      </Box>
                    </Tooltip>
                  );
                }

                const button = (
                  <ListItemButton
                    component={RouterLink}
                    to={nav.path}
                    selected={selected}
                    sx={{ borderRadius: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 34 }}>{nav.icon}</ListItemIcon>
                    <ListItemText primary={t(nav.label)} />
                    {nav.preview ? (
                      <UpcomingRoundedIcon
                        sx={{ fontSize: 15, color: comingSoon ? 'primary.main' : 'text.disabled' }}
                      />
                    ) : null}
                  </ListItemButton>
                );

                return tooltipTitle ? (
                  <Tooltip key={nav.key} title={tooltipTitle}>
                    <span>
                      {button}
                    </span>
                  </Tooltip>
                ) : (
                  button
                );
              })}
            </List>
          ))
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
